import React, { useEffect, useState } from "react";
import { Modal, Form, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import { FiClock, FiTag, FiTrendingUp, FiAlertCircle } from "react-icons/fi";
import theme from "../../styles/theme/theme";
import AppButton from "../buyer/AppButton";
import { useDispatch, useSelector } from "react-redux";
import { clearBuyerMessages, placeBid } from "../../features/buyer/buyerSlice";
import { showAppMessageToast } from "../../utils/toastUtils";
import { formatCurrency, formatPrice } from "../../utils/price";
import { formatBidDateTime } from "../../utils/timeAgo";
import { FaClock } from "react-icons/fa";

const PlaceBidModal = ({ show, onHide, product, onSubmit }) => {
  const [bidAmount, setBidAmount] = useState("");
  const [agreeDisclaimer, setAgreeDisclaimer] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [countdown, setCountdown] = useState(null);

  const dispatch = useDispatch();
  const error = useSelector((state) => state.buyer.bidError);
  const success = useSelector((state) => state.buyer.bidSuccess);

  // Example data (replace with backend)
  const lastBid = product?.last_bid_price;
  const minAskingPrice = product?.minimum_asking_price;
  const bidIncrement = product?.bid_increment;
  const bidCloseDate = product?.bid_closed_at;
  const currency = product?.currency || "";

  const TermsAndConditionsURL =
    "https://scraptrade.com.au/pages/slug/terms-and-conditions-scraptrade-com-au";

  const isBiddingClosed = bidCloseDate && new Date(bidCloseDate) <= new Date();

  // Map defined at component top level
  const bidInfoItems = [
    {
      label: "Last Bid",
      value: formatCurrency(lastBid),
      // lastBid != null ? `${currency} ${formatPrice(lastBid)}` : "-",
    },
    {
      label: "Minimum Asking",
      value: formatCurrency(minAskingPrice),
      // minAskingPrice != null
      //   ? `${currency} ${formatPrice(minAskingPrice)}`
      //   : "-",
    },
    {
      label: "Bid Increment",
      value: formatCurrency(bidIncrement),
      // bidIncrement != null ? `${currency} ${formatPrice(bidIncrement)}` : "-",
    },
    {
      label: "Bid Expire In",
      value: bidCloseDate ? formatBidDateTime(bidCloseDate) : "-",
    },
  ];

  // Reset modal when shown
  useEffect(() => {
    if (show) {
      dispatch(clearBuyerMessages());
      setBidAmount("");
      setAgreeDisclaimer(false);
      setValidationError("");
    }
  }, [show, dispatch]);

  // Success auto-close
  useEffect(() => {
    if (success) {
      setTimeout(() => {
        dispatch(clearBuyerMessages());
        onHide();
      }, 2000);
    }
  }, [success, dispatch, onHide]);

  // Countdown timer
  useEffect(() => {
    if (!bidCloseDate) return;
    const interval = setInterval(() => {
      const diff = new Date(bidCloseDate) - new Date();
      if (diff <= 0) {
        clearInterval(interval);
        setCountdown("Bidding Closed");
      } else {
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff / (1000 * 60)) % 60);
        const s = Math.floor((diff / 1000) % 60);
        setCountdown(`${h}h ${m}m ${s}s`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [bidCloseDate]);

  const handleSubmit = async () => {
    setValidationError("");

    if (!agreeDisclaimer) {
      setValidationError("Please accept the disclaimer before bidding.");
      return;
    }

    if (!bidAmount || isNaN(bidAmount) || Number(bidAmount) <= 0) {
      setValidationError("Please enter a valid bid amount.");
      return;
    }

    const bid = Number(bidAmount);

    if (minAskingPrice != null && bid < minAskingPrice) {
      setValidationError(
        `Bid must be at least ${currency} ${formatPrice(minAskingPrice)}.`,
      );
      return;
    }

    if (lastBid != null && bid < lastBid + bidIncrement) {
      setValidationError(
        `Your bid must be at least ${currency} ${formatPrice(
          lastBid + bidIncrement,
        )} (last bid + increment).`,
      );
      return;
    }

    if (
      lastBid != null &&
      bidIncrement &&
      (bid - lastBid) % bidIncrement !== 0
    ) {
      setValidationError(
        `Bid must increase in steps of ${currency} ${formatPrice(
          bidIncrement,
        )}.`,
      );
      return;
    }

    if (countdown === "Bidding Closed") {
      setValidationError("Bidding time is over.");
      return;
    }

    try {
      await dispatch(
        placeBid({ product_id: product.id, bid_price: Number(bidAmount) }),
      ).unwrap();

      showAppMessageToast({
        type: "success",
        title: "Bid Placed",
        message: "Your bid has been placed successfully.",
      });

      if (onSubmit)
        onSubmit({ product_id: product.id, bid_price: Number(bidAmount) });
      setBidAmount("");
    } catch (err) {
      console.error("Bid placement failed:", err);
      showAppMessageToast({
        type: "error",
        title: "Bid Failed",
        message: err || "Failed to place bid. Please try again.",
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md" backdrop="static">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}15)`,
          borderRadius: 16,
          boxShadow: `0 8px 20px ${theme.palette.secondary.main}30`,
        }}
      >
        {/* Header */}
        <Modal.Header
          closeButton
          style={{
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: "#fff",
          }}
        >
          <Modal.Title
            style={{
              fontWeight: 600,
              fontSize: "1.3rem",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <FiTrendingUp size={22} /> Submit Your Bid
          </Modal.Title>
        </Modal.Header>

        {isBiddingClosed && (
          <Alert
            variant="warning"
            className="mt-4 text-center fw-semibold"
            style={{
              fontSize: "1.05rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              marginLeft: "1.5rem",
              marginRight: "1.5rem",
            }}
          >
            <FaClock style={{ fontSize: "1.2rem" }} />
            Bidding on this product has ended.
          </Alert>
        )}

        {/* Body */}
        <Modal.Body
          style={{
            backdropFilter: "blur(12px)",
            backgroundColor: `${theme.palette.background.paper}CC`,
            borderRadius: "0 0 16px 16px",
            padding: "1.5rem",
          }}
        >
          {validationError && (
            <Alert
              variant="danger"
              style={{
                fontSize: "0.9rem",
                borderRadius: 8,
                backgroundColor: "#ffeaea",
              }}
            >
              <FiAlertCircle className="me-2" />
              {validationError}
            </Alert>
          )}

          {!isBiddingClosed && (
            <>
              {/* Info Cards */}
              <div
                className="d-grid mb-4"
                style={{
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "0.8rem",
                  display: "grid",
                }}
              >
                {bidInfoItems.map((item, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.03 }}
                    className="p-2"
                    style={{
                      borderRadius: 10,
                      background: `${theme.palette.background.default}99`,
                      border: `1px solid ${theme.palette.divider}`,
                      textAlign: "center",
                      boxShadow: `inset 0 0 6px ${theme.palette.secondary.main}10`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: "1rem",
                      }}
                    >
                      {item.value}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Countdown */}
              {bidCloseDate && !isBiddingClosed && countdown && (
                <motion.div
                  animate={{ scale: countdown === "Bidding Closed" ? 1.05 : 1 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 50,
                    padding: "6px 16px",
                    background:
                      countdown === "Bidding Closed"
                        ? "#ffebeb"
                        : `${theme.palette.success.light}30`,
                    color:
                      countdown === "Bidding Closed"
                        ? theme.palette.error.main
                        : theme.palette.success.main,
                    fontWeight: 600,
                    marginBottom: "1rem",
                    gap: "8px",
                    width: "fit-content",
                    marginInline: "auto",
                  }}
                >
                  <FiClock />
                  {countdown}
                </motion.div>
              )}
            </>
          )}

          {/* Bid Amount */}
          {!isBiddingClosed && (
            <Form.Group className="mb-4" controlId="bidAmount">
              <Form.Label style={{ fontWeight: 500 }}>
                Enter Your Bid Amount ({currency})
              </Form.Label>
              <Form.Control
                type="number"
                disabled={countdown === "Bidding Closed"}
                placeholder="Enter Your Bid Amount"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                style={{
                  borderRadius: 10,
                  padding: "12px 16px",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: `0 0 8px ${theme.palette.secondary.main}15`,
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) =>
                  (e.target.style.boxShadow = `0 0 12px ${theme.palette.primary.main}40`)
                }
                onBlur={(e) =>
                  (e.target.style.boxShadow = `0 0 8px ${theme.palette.secondary.main}15`)
                }
              />
            </Form.Group>
          )}

          {/* Disclaimer */}
          {!isBiddingClosed && (
            <Form.Check
              type="checkbox"
              id="disclaimer"
              disabled={countdown === "Bidding Closed"}
              label={
                <span style={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
                  I acknowledge that all bids are{" "}
                  <strong>final, legally binding</strong>, and subject to the{" "}
                  <a
                    href={TermsAndConditionsURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                    }}
                  >
                    Terms & Conditions
                  </a>
                  .
                </span>
              }
              checked={agreeDisclaimer}
              onChange={(e) => setAgreeDisclaimer(e.target.checked)}
            />
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: "none",
            backgroundColor: "transparent",
            padding: "1rem 1.5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
              width: "100%",
            }}
          >
            <AppButton
              variant="outlined"
              onClick={onHide}
              style={{ minWidth: 120 }}
            >
              {isBiddingClosed ? "Close" : "Cancel"}
            </AppButton>
            {!isBiddingClosed && (
              <AppButton
                variant="contained"
                onClick={handleSubmit}
                disabled={!agreeDisclaimer || countdown === "Bidding Closed"}
                style={{ minWidth: 140 }}
              >
                Place Bid
              </AppButton>
            )}
          </div>
        </Modal.Footer>
      </motion.div>
    </Modal>
  );
};

export default PlaceBidModal;
